import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CustomPageContext, PageTabs } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, Loading, PageHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Line, Text } from '~/styles';

interface Params {
  id: string;
}

export interface CardProgramProps {
  cardProgram: CardProgram;
}

const CardProgramHeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

export const PageCardProgram: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: cardProgram,
    isLoading,
    error: cardProgramError,
  } = useQuery({
    queryKey: ['card-program', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (cardProgramError) {
      navigate(ROUTE.CARD_PROGRAMS);
      addDangerNotification({
        content: (cardProgramError as any).message || 'Failed to load card program',
      });
    }
  }, [cardProgramError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (cardProgram ? { cardProgram } : undefined), [cardProgram]);

  return (
    <Loading isLoading={isLoading} fullHeight>
      <PageHeader
        text="Card Programs"
        backPath={ROUTE.CARD_PROGRAMS}
        tabs={<PageTabs entries={entries} />}
        backButton
      />
      {id && cardProgram && (
        <>
          <Inner pt={12} px="var(--page-margin)" pb={16}>
            <Flex flexDirection="column" gap={12}>
              <CardProgramHeaderRow>
                <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
                  {cardProgram.description || 'Card Program'}
                </Text>

                <InlineCopyInputWrapper>
                  <InlineCopyInput label="Card Program ID" value={cardProgram.id} minWidth="300px" isMonospaced />
                </InlineCopyInputWrapper>
              </CardProgramHeaderRow>
            </Flex>
          </Inner>
          <Line />
        </>
      )}
      {outletContext && <Outlet context={outletContext} />}
    </Loading>
  );
};
